import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, Save, Trash2, ExternalLink, Users, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";

import RichTextEditor from "@/components/RichTextEditor";
export default function ShowcasePanel() {
  const qc = useQueryClient();
  const cats = useQuery({ queryKey: ["showcase-cats"], queryFn: () => api.get("/showcase/categories").then((r) => r.data.categories) });
  const mine = useQuery({ queryKey: ["showcase-me"], queryFn: () => api.get("/showcase/me").then((r) => r.data) });
  const [form, setForm] = useState({ name: "", photo: "", bio: "", category: "" });
  const [saving, setSaving] = useState(false);
  const hasProfile = !!mine.data;

  useEffect(() => {
    if (mine.data) setForm({ name: mine.data.name || "", photo: mine.data.photo || "", bio: mine.data.bio || "", category: mine.data.category || "" });
  }, [mine.data]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Please add your name."); return; }
    setSaving(true);
    try {
      await api.post("/showcase/me", form);
      toast.success(hasProfile ? "Profile updated — it's live in the Showcase." : "You're now listed in the Community Showcase!");
      qc.invalidateQueries({ queryKey: ["showcase-me"] });
      qc.invalidateQueries({ queryKey: ["showcase-profiles"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove your Showcase profile? You can create a new one anytime.")) return;
    try {
      await api.delete("/showcase/me");
      toast.success("Profile removed from the Showcase.");
      qc.invalidateQueries({ queryKey: ["showcase-me"] });
      qc.invalidateQueries({ queryKey: ["showcase-profiles"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="eyw-card p-6 md:p-8" data-testid="showcase-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <p className="eyw-label mb-1 flex items-center gap-1.5"><Users size={13} /> Community Showcase</p>
          <h2 className="font-serif text-2xl text-[#1A242B]">Your Showcase profile</h2>
          <p className="text-sm text-[#4A5D6B] mt-1">Share your gifts with the community. {hasProfile && <span className="inline-flex items-center gap-1 text-[#5C8A72] font-medium"><Eye size={13} /> Live</span>}</p>
          {hasProfile && <p className="text-xs text-[#8FA89B] mt-1">Members who reach out start a conversation in your <Link to="/messages" className="text-[#144F75] underline" data-testid="showcase-inbox-link">Messages inbox</Link>.</p>}
        </div>
        <Link to="/showcase" target="_blank" data-testid="showcase-view-directory" className="eyw-btn-ghost !py-2 text-sm"><ExternalLink size={15} /> View directory</Link>
      </div>

      {mine.isLoading ? (
        <div className="py-10 grid place-items-center text-[#8FA89B]"><Loader2 className="animate-spin" size={22} /></div>
      ) : (
        <div className="space-y-4 max-w-xl">
          <div>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Display name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="showcase-name" className="eyw-input w-full" />
          </div>
          <div>
            <ImageUpload value={form.photo} onChange={(url) => setForm({ ...form, photo: url })} label="Profile photo" rounded testid="showcase-photo" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Skill / talent category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="showcase-category" className="eyw-input w-full">
              <option value="">Choose a category…</option>
              {(cats.data || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">Short bio</label>
            <RichTextEditor value={form.bio} onChange={(v) => setForm((f) => ({ ...f, bio: v }))} testid="showcase-bio" placeholder="Tell the community about your gifts, background and what you love to share…" minHeight={120} enableBlocks={false} media={false} />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving} data-testid="showcase-save" className="eyw-btn !py-2.5 text-sm disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={15} />} {hasProfile ? "Save changes" : "Publish my profile"}
            </button>
            {hasProfile && (
              <button onClick={remove} data-testid="showcase-remove" className="eyw-btn-ghost !py-2.5 text-sm !border-red-300 !text-red-600 hover:!bg-red-500"><Trash2 size={15} /> Remove</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
